import React, { useState, useEffect, useRef } from 'react';
import { isStaff } from '../../utils/permissions';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import SegToggle from '../../components/Common/SegToggle';
import { forgotPassword } from '../../services/authService';
import { getOverviewByCode } from '../../services/publicService';
import StudentProfileModal from '../../components/Admin/StudentProfileModal';

const ASSET = (name) => `${process.env.PUBLIC_URL || ''}/landing/${name}`;

/* ── Демо-база учеников для родительского раздела (пока только дизайн, без API) ── */
const AVA = ['#7c4dff', '#22b8cf', '#059669', '#d97706', '#7c3aed', '#dc2626', '#0284c7', '#16a34a'];
const initials = (n) => n.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
const avaColor = (id) => AVA[id % AVA.length];

const STUDENTS = [
  {
    id: 3, name: 'Иванов Артём', email: 'artem.iv@example.com', course: 'ЕГЭ · Информатика',
    since: 'сентябрь 2025', progress: 74, lessonsDone: 18, lessonsTotal: 24,
    hwAccepted: 31, hwPending: 2, hwRejected: 3, avgGrade: '4,6',
    modules: [
      { name: 'Сентябрь · Введение', pct: 100, c: '#16a34a' },
      { name: 'Октябрь · Алгоритмы', pct: 92, c: '#16a34a' },
      { name: 'Ноябрь · Программирование', pct: 68, c: '#7c4dff' },
      { name: 'Декабрь · Базы данных', pct: 34, c: '#d97706' },
      { name: 'Январь · Сети', pct: 0, c: '#9690a8' },
    ],
    hw: [
      { t: 'Задание №12 · Рекурсия', d: '2 дня назад', s: 'accept' },
      { t: 'Задание №11 · Циклы', d: '4 дня назад', s: 'accept' },
      { t: 'Задание №3 · Теория вероятностей', d: 'сегодня', s: 'pending' },
      { t: 'Задание №9 · Системы счисления', d: '6 дней назад', s: 'reject' },
      { t: 'Задание №8 · Логика', d: 'неделю назад', s: 'accept' },
    ],
  },
  {
    id: 5, name: 'Иванова Мария', email: 'maria.iv@example.com', course: 'ОГЭ · Информатика',
    since: 'октябрь 2025', progress: 58, lessonsDone: 11, lessonsTotal: 20,
    hwAccepted: 19, hwPending: 1, hwRejected: 2, avgGrade: '4,2',
    modules: [
      { name: 'Сентябрь · Основы', pct: 100, c: '#16a34a' },
      { name: 'Октябрь · Кодирование', pct: 80, c: '#16a34a' },
      { name: 'Ноябрь · Таблицы', pct: 55, c: '#7c4dff' },
      { name: 'Декабрь · Алгоритмы', pct: 20, c: '#d97706' },
    ],
    hw: [
      { t: 'Задание №7 · Кодирование текста', d: '1 день назад', s: 'accept' },
      { t: 'Задание №6 · Электронные таблицы', d: '3 дня назад', s: 'pending' },
      { t: 'Задание №5 · Файловая система', d: '5 дней назад', s: 'accept' },
      { t: 'Задание №4 · Перевод систем', d: 'неделю назад', s: 'accept' },
    ],
  },
];

const STATUS = {
  accept:  { lbl: 'Принято',     c: '#34d399', bg: 'rgba(52,211,153,.16)', dot: '#34d399' },
  pending: { lbl: 'На проверке', c: '#fbbf24', bg: 'rgba(251,191,36,.16)', dot: '#fbbf24' },
  reject:  { lbl: 'Отклонено',   c: '#f87171', bg: 'rgba(248,113,113,.16)', dot: '#f87171' },
};

const IcLayers = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>
);
const IcClock = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>
);
const IcParents = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
);

const StatCard = ({ v, l, c, bg }) => (
  <div className="stat-card" style={{ background: bg }}>
    <div className="stat-val" style={{ color: c }}>{v}</div>
    <div className="stat-lbl">{l}</div>
  </div>
);

const LandingPage = () => {
  const [mode, setMode]         = useState('login'); // 'login' | 'register'
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [agreePolicy, setAgreePolicy]   = useState(false);
  const [agreeConsent, setAgreeConsent] = useState(false);
  const [regStep, setRegStep]   = useState(0);       // 0 — имя/фамилия, 1 — email/пароль/согласия
  const [regDir, setRegDir]     = useState('fwd');   // направление анимации перехода
  const [forgot, setForgot]     = useState(false);   // режим «забыли пароль»
  const [forgotSent, setForgotSent] = useState(false);

  // Родительский раздел
  const [pQuery, setPQuery]     = useState('');
  const [results, setResults]   = useState(null); // null = ещё не искали
  const [overview, setOverview] = useState(null); // карточка ученика по коду
  const [ovLoading, setOvLoading] = useState(false);
  const [childId, setChildId]   = useState(null);
  const parentRef = useRef(null);
  const searchRef = useRef(null);

  const { login, register, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && user) navigate(isStaff(user.role) ? '/admin' : '/home');
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    const saved = localStorage.getItem('rememberedEmail');
    if (saved) setEmail(saved);
  }, []);


  const handleSubmit = async (e) => {
    e.preventDefault();
    const mail = email.trim();

    // Валидация в нашем стиле (тосты на русском) — вместо англоязычных
    // нативных подсказок браузера («Please fill in this field» и т.п.).
    if (forgot) {
      if (!mail) return toast.error('Введите email');
    } else if (mode === 'login') {
      if (!mail) return toast.error('Введите email');
      if (!password) return toast.error('Введите пароль');
    } else {
      if (!mail) return toast.error('Введите email');
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) return toast.error('Введите корректный email');
      if (password.length < 6) return toast.error('Пароль должен быть не короче 6 символов');
      if (!agreePolicy || !agreeConsent) return toast.error('Подтвердите согласие с условиями');
    }

    setLoading(true);
    try {
      if (forgot) {
        await forgotPassword(mail);
        setForgotSent(true);
      } else if (mode === 'login') {
        const result = await login(mail, password);
        localStorage.setItem('rememberedEmail', mail);
        navigate(isStaff(result?.user?.role) ? '/admin' : '/home');
      } else {
        await register({ name, email: mail, password });
        navigate('/home');
      }
    } catch (err) {
      // «Забыли пароль?» идёт не через AuthContext — показываем ошибку здесь
      // (например «Аккаунт с таким email не найден»). Вход/регистрацию тостит AuthContext.
      if (forgot) toast.error(err?.response?.data?.message || 'Не удалось отправить письмо');
    } finally {
      setLoading(false);
    }
  };

  // Шаг 1 регистрации валиден, когда указаны и имя, и фамилия.
  // В бэкенд отправляем в порядке «Имя Фамилия», как бы ученик их ни ввёл.
  const name = `${firstName.trim()} ${lastName.trim()}`.trim();
  const nameValid = firstName.trim().length >= 1 && lastName.trim().length >= 1;
  const switchMode = (m) => { setMode(m); setRegStep(0); setRegDir('fwd'); };
  const goNext = () => { if (nameValid) { setRegDir('fwd'); setRegStep(1); } };
  const goBack = () => { setRegDir('back'); setRegStep(0); };
  const onNameKey = (e) => { if (e.key === 'Enter') { e.preventDefault(); goNext(); } };

  const gotoParent = () => parentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  /* Ссылка для родителя infasotka.online/p/КОД открывает карточку прямо здесь,
     поверх лендинга: закрыв её, родитель остаётся на сайте, а не на пустой
     странице. Без кода (/p) просто подводим к полю ввода. */
  const { code: codeParam } = useParams();
  useEffect(() => {
    const code = (codeParam || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
    if (code.length !== 4) {
      if (window.location.pathname.replace(/\/$/, '') === '/p') gotoParent();
      return;
    }
    setPQuery(code);
    setOvLoading(true);
    getOverviewByCode(code)
      .then(r => { if (r?.data?.student) setOverview(r.data); else toast.error('Ученик с таким кодом не найден'); })
      .catch(e => toast.error(e?.response?.data?.message || 'Ученик с таким кодом не найден'))
      .finally(() => setOvLoading(false));
    // eslint-disable-next-line
  }, [codeParam]);

  // Поиск успеваемости по коду ученика — карточка открывается окном прямо тут,
  // без перехода на отдельную страницу.
  const runSearch = () => {
    const code = pQuery.trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
    setChildId(null);
    if (code.length !== 4) { setResults([]); return; } // подсказка «введите 4 символа»
    setResults(null);
    setOvLoading(true);
    getOverviewByCode(code)
      .then(r => { if (r?.data?.student) setOverview(r.data); else toast.error('Ученик с таким кодом не найден'); })
      .catch(e => toast.error(e?.response?.data?.message || 'Ученик с таким кодом не найден'))
      .finally(() => setOvLoading(false));
  };

  // Карточка ученика нарисована под тёмную тему — включаем её на время показа
  // и возвращаем как было (у гостя на лендинге тема обычно светлая).
  useEffect(() => {
    if (!overview) return;
    const had = document.body.classList.contains('dark');
    if (!had) document.body.classList.add('dark');
    return () => { if (!had) document.body.classList.remove('dark'); };
  }, [overview]);

  const backToSearch = () => {
    setChildId(null);
    searchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const child = childId != null ? STUDENTS.find(s => s.id === childId) : null;

  return (
    <div className="lp">
      <style>{`
        @font-face { font-family: 'Saar SP Demo'; src: url(${ASSET('fonts/SaarSPDemo.otf')}) format('opentype'); font-weight: normal; font-display: block; }
        @font-face { font-family: 'Montserrat'; src: url(${ASSET('fonts/Montserrat-Light.ttf')}) format('truetype'); font-weight: 300; font-display: block; }
        @font-face { font-family: 'Montserrat'; src: url(${ASSET('fonts/Montserrat-Regular.ttf')}) format('truetype'); font-weight: 400; font-display: block; }
        @font-face { font-family: 'Montserrat'; src: url(${ASSET('fonts/Montserrat-Medium.ttf')}) format('truetype'); font-weight: 500; font-display: block; }
        @font-face { font-family: 'Montserrat'; src: url(${ASSET('fonts/Montserrat-Bold.ttf')}) format('truetype'); font-weight: 700; font-display: block; }

        .lp { position: relative; min-height: 100vh; overflow: hidden; background: #0a0612;
              font-family: 'Montserrat', -apple-system, sans-serif; color: #fff; }
        /* Верхний слайд: вся атмосфера (паттерн/свечение/виньетка) живёт только здесь,
           поэтому низ геро уходит в чистый чёрный, а родительский раздел — чёрный сверху. */
        .lp-top { position: relative; overflow: hidden; }
        /* Затемнение низа геро на ВСЮ ширину (а не только центральную колонку) —
           иначе по краям оставалась светлая полоса на стыке со вторым слайдом. */
        .lp-top::after { content: ''; position: absolute; left: 0; right: 0; bottom: 0; height: 34%; z-index: 3;
          pointer-events: none; background: linear-gradient(to bottom, rgba(10,6,18,0) 0%, rgba(10,6,18,0.5) 58%, #0a0612 94%, #0a0612 100%); }

        /* Фоновые слои */
        .lp-pattern, .lp-glow, .lp-vign { position: absolute; inset: 0; pointer-events: none; }
        .lp-pattern {
          background: url(${ASSET('pattern-brand.png')}) 0 -10px / 300px auto repeat;
          opacity: 0.55; mix-blend-mode: screen;
          -webkit-mask-image: radial-gradient(120% 70% at 32% 28%, #000 0%, rgba(0,0,0,0.35) 50%, transparent 80%);
                  mask-image: radial-gradient(120% 70% at 32% 28%, #000 0%, rgba(0,0,0,0.35) 50%, transparent 80%);
        }
        .lp-glow {
          background:
            radial-gradient(58% 42% at 38% 26%, rgba(124,58,237,0.46) 0%, rgba(124,58,237,0) 60%),
            radial-gradient(45% 35% at 85% 48%, rgba(109,40,217,0.40) 0%, rgba(109,40,217,0) 60%),
            radial-gradient(38% 30% at 12% 14%, rgba(124,77,255,0.18) 0%, rgba(124,77,255,0) 62%);
        }
        .lp-vign { box-shadow: inset 0 0 220px 60px rgba(0,0,0,0.7); }

        /* Шапка — на всю ширину: лого у левого края, надпись у правого */
        .lp-head { position: relative; z-index: 5; display: flex; align-items: center;
                   justify-content: space-between; gap: 16px; padding: 24px clamp(20px, 3.4vw, 46px); }
        .lp-logo-img { height: 34px; width: auto; display: block; filter: brightness(0) invert(1); flex-shrink: 0; }
        .lp-head-note span { font-size: 12.5px; color: #b1a8cf; text-align: right; }

        /* Hero */
        .lp-main { position: relative; z-index: 4; max-width: 1280px; margin: 0 auto;
                   /* Высота геро ФИКСИРОВАНА, чтобы при переключении Вход/Регистрация
                      не рос блок и не «прыгали» фото Артёма и секции ниже. */
                   height: max(calc(100vh - 90px), 760px); padding: 0 40px 40px;
                   display: flex; flex-direction: column; }
        .lp-photo { position: absolute; left: -52px; bottom: 0; height: 104%; max-height: 920px; width: auto;
                    object-fit: contain; z-index: 2; filter: drop-shadow(0 30px 60px rgba(0,0,0,0.55));
                    pointer-events: none; user-select: none;
                    -webkit-mask-image: linear-gradient(to bottom, #000 0%, #000 82%, transparent 100%);
                            mask-image: linear-gradient(to bottom, #000 0%, #000 82%, transparent 100%); }
        .lp-arrow { position: absolute; right: 34px; top: 136px; width: 138px; height: auto; z-index: 4;
                    opacity: 0.9; pointer-events: none; user-select: none; }

        .lp-content { position: relative; z-index: 4; margin-left: 46%; max-width: 600px; padding-top: 24px; }
        .lp-eyebrow { font-family: 'Saar SP Demo', sans-serif; font-size: 15px; letter-spacing: 2px;
                      color: #cbb8f0; text-transform: uppercase; margin-bottom: 14px; }
        .lp-title { font-family: 'Saar SP Demo', sans-serif; font-size: 46px; line-height: 1.05; font-weight: 400;
                    text-transform: uppercase; margin: 0 0 18px; text-shadow: 0 4px 30px rgba(0,0,0,0.5); }
        .lp-sub { display: flex; align-items: center; flex-wrap: wrap; gap: 9px; font-size: 15px; font-weight: 500;
                  letter-spacing: 0.4px; text-transform: uppercase; color: #e9e3f7; margin-bottom: 26px; }
        .lp-pill { display: inline-flex; align-items: center; padding: 4px 14px; border-radius: 30px; font-weight: 700;
                   border: 1.5px solid #8b5cf6; color: #fff; box-shadow: 0 0 18px rgba(139,92,246,0.6); }

        /* Окно входа/регистрации */
        .lp-login { width: 100%; max-width: 440px; background: rgba(255,255,255,0.06);
                    border: 1px solid rgba(255,255,255,0.12); border-radius: 18px; padding: 22px;
                    backdrop-filter: blur(12px); box-shadow: 0 20px 50px rgba(0,0,0,0.4);
                    /* Высота — по контенту: «Вход» меньше, «Регистрация» больше.
                       Геро при этом не растёт (его высота зафиксирована выше). */
                    box-sizing: border-box; }
        .lp-login .seg { margin-bottom: 18px; }

        .lp-field { margin-bottom: 13px; }
        .lp-field label { display: block; font-size: 11px; font-weight: 700; letter-spacing: 0.5px;
                          text-transform: uppercase; color: #a99fc7; margin-bottom: 6px; }
        .lp-input { width: 100%; padding: 12px 14px; box-sizing: border-box; background: rgba(0,0,0,0.30);
                    border: 1px solid rgba(255,255,255,0.15); border-radius: 11px; color: #fff; font-size: 14px;
                    outline: none; transition: border 0.15s; font-family: inherit; }
        .lp-input::placeholder { color: #7c7396; }
        .lp-input:focus { border-color: #8b5cf6; }
        .lp-eye { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); background: none;
                  border: none; cursor: pointer; color: #8a82a6; display: flex; padding: 4px; }
        .lp-eye svg { width: 18px; height: 18px; }
        .lp-hint { font-size: 11px; color: #7c7396; margin-top: 5px; }
        .lp-btn { width: 100%; padding: 14px; margin-top: 6px; border: none; border-radius: 30px; cursor: pointer;
                  background: linear-gradient(180deg, #7c4dff 0%, #6d28d9 100%); color: #fff; font-size: 13px;
                  font-weight: 700; letter-spacing: 1px; text-transform: uppercase; font-family: inherit;
                  box-shadow: 0 0 26px rgba(124,77,255,0.6); transition: 0.18s; }
        .lp-btn:hover:not(:disabled) { box-shadow: 0 0 36px rgba(124,77,255,0.9); transform: translateY(-1px); }
        .lp-btn:disabled { opacity: 0.7; cursor: not-allowed; }
        .lp-foot { margin-top: 14px; font-size: 13px; color: #a99fc7; text-align: center; }
        .lp-foot button { background: none; border: none; color: #b794f6; font-weight: 700; cursor: pointer;
                          font-family: inherit; font-size: 13px; padding: 0; }
        .lp-foot button:hover { text-decoration: underline; }

        /* Двухшаговая регистрация */
        .lp-steps { display: flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 18px; }
        .lp-stepdot { width: 9px; height: 9px; border-radius: 50%; background: rgba(255,255,255,0.18); transition: 0.3s; }
        .lp-stepdot.on { background: #8b5cf6; box-shadow: 0 0 10px rgba(139,92,246,0.85); }
        .lp-stepbar { width: 34px; height: 2px; border-radius: 2px; background: rgba(255,255,255,0.14); transition: 0.3s; }
        .lp-stepbar.on { background: #8b5cf6; }
        .lp-stepwrap { animation: lpStepIn 0.34s cubic-bezier(.22,.61,.36,1); }
        .lp-stepwrap[data-dir="back"] { animation: lpStepInBack 0.34s cubic-bezier(.22,.61,.36,1); }
        @keyframes lpStepIn { from { opacity: 0; transform: translateX(28px); } to { opacity: 1; transform: none; } }
        @keyframes lpStepInBack { from { opacity: 0; transform: translateX(-28px); } to { opacity: 1; transform: none; } }
        .lp-next { display: flex; align-items: center; justify-content: center; gap: 10px; width: 100%; padding: 14px;
                   margin-top: 6px; border: none; border-radius: 30px; cursor: pointer; font-family: inherit;
                   background: linear-gradient(180deg, #7c4dff 0%, #6d28d9 100%); color: #fff; font-size: 13px;
                   font-weight: 700; letter-spacing: 1px; text-transform: uppercase;
                   box-shadow: 0 0 26px rgba(124,77,255,0.6); transition: 0.2s; }
        .lp-next:disabled { background: rgba(255,255,255,0.07); color: #8a82a6; box-shadow: none; cursor: not-allowed; }
        .lp-next:not(:disabled):hover { transform: translateY(-1px); box-shadow: 0 0 38px rgba(124,77,255,0.95); }
        .lp-next svg { width: 18px; height: 18px; transition: transform 0.2s; }
        .lp-next:not(:disabled):hover svg { transform: translateX(4px); }
        .lp-back { display: inline-flex; align-items: center; gap: 6px; background: none; border: none; padding: 0;
                   margin-bottom: 12px; color: #a99fc7; font-family: inherit; font-size: 12.5px; font-weight: 600;
                   cursor: pointer; transition: color 0.15s; }
        .lp-back:hover { color: #fff; }
        .lp-back svg { width: 15px; height: 15px; }
        .lp-greet { font-size: 13.5px; color: #d9cff5; margin-bottom: 14px; }
        .lp-greet b { color: #fff; font-weight: 700; }

        /* Кнопка для родителей — только на вкладке «Вход» */
        .lp-parent { display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%;
                     margin-top: 14px; padding: 11px 14px; border-radius: 16px; cursor: pointer; font-family: inherit;
                     background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.16);
                     color: #d9cff5; font-size: 12.5px; font-weight: 600; letter-spacing: 0; transition: 0.18s;
                     text-align: center; line-height: 1.3; }
        .lp-parent:hover { background: rgba(124,77,255,0.16); border-color: #8b5cf6; color: #fff;
                           box-shadow: 0 0 22px rgba(124,77,255,0.35); }
        .lp-parent svg { width: 17px; height: 17px; flex-shrink: 0; }

        /* Индикатор «листай вниз — для родителей» */
        .lp-scrollcue { align-self: center; margin-top: auto; padding-top: 26px; z-index: 6;
                        display: flex; flex-direction: column; align-items: center; gap: 6px; color: #9b90c4;
                        font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase; cursor: pointer;
                        background: none; border: none; font-family: inherit; }
        .lp-scrollcue svg { animation: lpbob 2s ease-in-out infinite; }
        .lp-scrollcue:hover { color: #cbb8f0; }
        .lp-scrollcue svg { width: 20px; height: 20px; }
        @keyframes lpbob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(6px); } }

        /* ── Родительский раздел (отдельный слайд на весь экран) ── */
        .parent { position: relative; z-index: 4; min-height: 100vh; display: flex; flex-direction: column;
                  justify-content: center; padding: 90px 40px; background: #0a0612; }
        /* Свечение-градиент в нижней части раздела (сверху — чистый чёрный) */
        .parent::before { content: ''; position: absolute; left: 0; right: 0; bottom: 0; height: 52%; z-index: 1; pointer-events: none;
          background:
            radial-gradient(72% 82% at 50% 100%, rgba(124,58,237,0.26) 0%, rgba(124,58,237,0) 70%),
            radial-gradient(42% 56% at 82% 100%, rgba(109,40,217,0.18) 0%, rgba(109,40,217,0) 72%); }
        .parent-inner { max-width: 980px; margin: 0 auto; position: relative; z-index: 2; }
        .parent-head { text-align: center; margin-bottom: 38px; }
        .parent-tag { display: inline-flex; align-items: center; gap: 7px; padding: 6px 16px; border-radius: 30px;
                      border: 1px solid rgba(139,92,246,0.5); background: rgba(124,77,255,0.1); color: #cbb8f0;
                      font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 18px; }
        .parent-tag svg { width: 15px; height: 15px; }
        .parent-title { font-family: 'Saar SP Demo', sans-serif; font-size: 38px; line-height: 1.08; font-weight: 400;
                        text-transform: uppercase; margin-bottom: 14px; }
        .parent-title span { color: #b794f6; }
        .parent-lead { font-size: 15px; color: #b8aed4; max-width: 560px; margin: 0 auto; line-height: 1.6; }

        .search-card { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12);
                       border-radius: 22px; padding: 30px; backdrop-filter: blur(14px);
                       box-shadow: 0 24px 60px rgba(0,0,0,0.4); max-width: 680px; margin: 0 auto; }
        .sc-label { font-size: 11px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase;
                    color: #a99fc7; margin-bottom: 9px; }
        .search-row { display: flex; gap: 10px; }
        .search-input-wrap { position: relative; flex: 1; }
        .search-input-wrap svg { position: absolute; left: 15px; top: 50%; transform: translateY(-50%);
                                 width: 18px; height: 18px; color: #7c7396; pointer-events: none; }
        .search-input { width: 100%; padding: 14px 14px 14px 44px; box-sizing: border-box; background: rgba(0,0,0,0.3);
                        border: 1px solid rgba(255,255,255,0.15); border-radius: 13px; color: #fff; font-size: 15px;
                        outline: none; transition: border 0.15s; font-family: inherit; }
        .search-input::placeholder { color: #7c7396; }
        .search-input:focus { border-color: #8b5cf6; }
        .search-btn { padding: 0 26px; border: none; border-radius: 13px; cursor: pointer;
                      background: linear-gradient(180deg,#7c4dff,#6d28d9); color: #fff; font-size: 14px; font-weight: 700;
                      box-shadow: 0 0 22px rgba(124,77,255,0.5); transition: 0.18s; white-space: nowrap; }
        .search-btn:hover { transform: translateY(-1px); box-shadow: 0 0 30px rgba(124,77,255,0.8); }
        .search-note { font-size: 12px; color: #7c7396; margin-top: 12px; display: flex; align-items: center; gap: 6px; }
        .search-note svg { width: 14px; height: 14px; flex-shrink: 0; }

        .results { margin: 22px auto 0; max-width: 680px; display: flex; flex-direction: column; gap: 10px; }
        .results-label { font-size: 12px; color: #9b90c4; margin-bottom: 2px; }
        .results-label b { color: #fff; }
        .res-card { display: flex; align-items: center; gap: 14px; padding: 14px 16px; border-radius: 16px;
                    background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); cursor: pointer;
                    transition: 0.18s; text-align: left; width: 100%; font-family: inherit; }
        .res-card:hover { background: rgba(124,77,255,0.14); border-color: #8b5cf6;
                          transform: translateY(-1px); box-shadow: 0 10px 30px rgba(0,0,0,0.35); }
        .res-avatar { width: 46px; height: 46px; border-radius: 13px; flex-shrink: 0; display: flex;
                      align-items: center; justify-content: center; color: #fff; font-weight: 800; font-size: 16px; }
        .res-info { flex: 1; min-width: 0; }
        .res-name { font-size: 15px; font-weight: 700; color: #fff; }
        .res-meta { font-size: 12.5px; color: #a99fc7; margin-top: 2px; }
        .res-go { color: #b794f6; display: flex; flex-shrink: 0; }
        .res-go svg { width: 20px; height: 20px; }
        .res-empty { text-align: center; padding: 26px; color: #9b90c4; font-size: 14px; }

        /* Дашборд ребёнка */
        .child { max-width: 880px; margin: 22px auto 0; background: rgba(255,255,255,0.05);
                 border: 1px solid rgba(255,255,255,0.12); border-radius: 24px; padding: 28px;
                 backdrop-filter: blur(16px); box-shadow: 0 28px 70px rgba(0,0,0,0.5); }
        .child-back { display: inline-flex; align-items: center; gap: 7px; background: none; border: none; cursor: pointer;
                      color: #a99fc7; font-size: 13px; font-weight: 600; margin-bottom: 20px; transition: 0.15s; font-family: inherit; }
        .child-back:hover { color: #fff; }
        .child-back svg { width: 16px; height: 16px; }
        .child-head { display: flex; align-items: center; gap: 18px; margin-bottom: 24px; }
        .child-head > div:last-child { flex: 1; min-width: 0; }
        .child-avatar { width: 74px; height: 74px; border-radius: 20px; flex-shrink: 0; display: flex;
                        align-items: center; justify-content: center; color: #fff; font-size: 28px; font-weight: 800; }
        .child-name { font-size: 23px; font-weight: 700; color: #fff; }
        .child-email { font-size: 13.5px; color: #a99fc7; margin: 3px 0 9px; }
        .child-badge { display: inline-flex; align-items: center; padding: 4px 13px; border-radius: 20px;
                       font-size: 12px; font-weight: 700; background: rgba(124,77,255,0.2); color: #c4b1f5;
                       border: 1px solid rgba(139,92,246,0.4); }
        .stat-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 12px; margin-bottom: 22px; }
        .stat-card { border-radius: 16px; padding: 18px 16px; text-align: center; border: 1px solid rgba(255,255,255,0.08); }
        .stat-val { font-size: 27px; font-weight: 800; line-height: 1; }
        .stat-lbl { font-size: 11.5px; color: #b8aed4; margin-top: 7px; }
        .child-bar-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 8px; }
        .child-bar-head .t { font-size: 13px; color: #b8aed4; }
        .child-bar-head .v { font-size: 13px; font-weight: 700; color: #fff; }
        .child-bar { width: 100%; height: 9px; background: rgba(255,255,255,0.1); border-radius: 10px; overflow: hidden; margin-bottom: 26px; }
        .child-bar > i { display: block; height: 100%; border-radius: 10px; background: linear-gradient(180deg,#7c4dff,#6d28d9);
                         box-shadow: 0 0 14px rgba(124,77,255,0.6); }
        .child-cols { display: grid; grid-template-columns: 1.1fr 1fr; gap: 18px; }
        .panel { background: rgba(0,0,0,0.22); border: 1px solid rgba(255,255,255,0.08); border-radius: 16px; padding: 18px; }
        .panel-title { font-size: 12.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px;
                       color: #cbb8f0; margin-bottom: 14px; display: flex; align-items: center; gap: 7px; }
        .panel-title svg { width: 15px; height: 15px; }
        .mod-row { margin-bottom: 13px; }
        .mod-row:last-child { margin-bottom: 0; }
        .mod-row-head { display: flex; justify-content: space-between; margin-bottom: 5px; }
        .mod-name { font-size: 13px; color: #e6dffb; }
        .mod-pct { font-size: 12px; font-weight: 700; color: #b8aed4; }
        .mini-bar { width: 100%; height: 6px; background: rgba(255,255,255,0.09); border-radius: 8px; overflow: hidden; }
        .mini-bar > i { display: block; height: 100%; border-radius: 8px; }
        .hw-item { display: flex; align-items: center; gap: 11px; padding: 9px 0; border-bottom: 1px solid rgba(255,255,255,0.07); }
        .hw-item:last-child { border-bottom: none; }
        .hw-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
        .hw-info { flex: 1; min-width: 0; }
        .hw-title { font-size: 13px; color: #e6dffb; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .hw-date { font-size: 11px; color: #8a82a6; margin-top: 1px; }
        .hw-status { font-size: 11px; font-weight: 700; padding: 3px 9px; border-radius: 20px; white-space: nowrap; }
        .hw-legend { margin-top: 14px; display: flex; gap: 12px; flex-wrap: wrap; font-size: 11.5px; color: #9b90c4; }

        /* Футер */
        .lp-footer { position: relative; z-index: 4; border-top: 1px solid rgba(255,255,255,0.08);
                     padding: 48px clamp(20px, 3.4vw, 46px) 40px; }
        .footer-inner { margin: 0; display: flex; align-items: center;
                        justify-content: space-between; gap: 40px; flex-wrap: wrap; }
        .footer-legal { font-size: 13px; line-height: 1.7; color: #9b90c4; }
        .footer-legal b { color: #d9cff5; font-weight: 600; }
        .footer-logo img { height: 50px; width: auto; opacity: 0.92; filter: brightness(0) invert(1); }
        .footer-docs { display: flex; flex-direction: column; align-items: flex-end; gap: 10px; text-align: right; }
        .footer-docs-title { font-size: 11px; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase;
                             color: #6f6690; margin-bottom: 2px; }
        .footer-docs a { display: inline-flex; align-items: center; gap: 7px; font-size: 13.5px; color: #b9aee6;
                         text-decoration: none; transition: color 0.15s, gap 0.15s; }
        .footer-docs a svg { width: 13px; height: 13px; opacity: 0.55; transition: opacity 0.15s, transform 0.15s; }
        .footer-docs a:hover { color: #fff; gap: 10px; }
        .footer-docs a:hover svg { opacity: 1; transform: translateX(2px); }
        @media (max-width: 680px) { .footer-docs { align-items: flex-start; text-align: left; } }
        .footer-bottom { margin: 30px 0 0; padding-top: 22px;
                         border-top: 1px solid rgba(255,255,255,0.06); display: flex; justify-content: space-between;
                         flex-wrap: wrap; gap: 10px; font-size: 12px; color: #6f6690; }

        @media (max-width: 1040px) {
          .lp-photo { opacity: 0.2; height: 78%; left: -70px; }
          .lp-content { margin-left: 0; padding-top: 24px; }
          .lp-title { font-size: 36px; }
          .lp-arrow { display: none; }
          .child-cols { grid-template-columns: 1fr; }
        }
        @media (max-width: 680px) {
          .lp-head { padding: 16px 18px; }
          .lp-head-note span { font-size: 11px; }
          .lp-main { padding: 0 20px 30px; height: auto; min-height: 0; }
          .lp-login { max-width: 100%; }
          .lp-title { font-size: 29px; }
          .parent { padding: 70px 20px 48px; }
          .parent-title { font-size: 27px; }
          .search-row { flex-direction: column; }
          .search-btn { padding: 14px; }
          .stat-grid { grid-template-columns: repeat(2,1fr); }
          .footer-inner { flex-direction: column-reverse; align-items: flex-start; }
        }
      `}</style>

      <div className="lp-top">
      <div className="lp-pattern" />
      <div className="lp-glow" />
      <div className="lp-vign" />

      <header className="lp-head">
        <img className="lp-logo-img" src={ASSET('logo-hq.png')} alt="ИНФА СОТКА" />
        <div className="lp-head-note">
          <span>Подготовка к ОГЭ / ЕГЭ по информатике</span>
        </div>
      </header>

      {/* ── HERO ── */}
      <div className="lp-main">
        <img className="lp-photo" src={ASSET('photo.png')} alt="Артём Александрович" />

        <div className="lp-content">
          <div className="lp-eyebrow">Артём Александрович · преподаватель</div>
          {/* Перенос жёсткий: на средних ширинах заголовок влезал в одну строку
              и «по информатике» оказывалось на первой */}
          <h1 className="lp-title">Онлайн подготовка к ОГЭ/ЕГЭ<br />по информатике</h1>
          <div className="lp-sub">
            Сдай на <span className="lp-pill">80+</span> без скуки, зубрёжки и паники
          </div>

          <img className="lp-arrow" src={ASSET('arrow.svg')} alt="" />

          <div className="lp-login">
            {forgot ? (
              forgotSent ? (
                <div style={{ textAlign: 'center', padding: '8px 4px' }}>
                  <div style={{ fontSize: 17, fontWeight: 800, color: '#fff', marginBottom: 8 }}>Письмо отправлено</div>
                  <p style={{ fontSize: 13.5, color: '#c9bff0', lineHeight: 1.5, marginBottom: 12 }}>
                    Мы отправили ссылку для сброса пароля на <b style={{ color: '#fff' }}>{email}</b>.
                  </p>
                  <p style={{ fontSize: 12.5, color: '#a99fc7', lineHeight: 1.5, marginBottom: 18 }}>
                    Не видите письмо? Проверьте папку <b style={{ color: '#c9bff0' }}>«Спам»</b> — иногда оно попадает туда.
                  </p>
                  <button type="button" className="lp-btn" onClick={() => { setForgot(false); setForgotSent(false); }}>Вернуться ко входу</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate>
                  <div style={{ fontSize: 17, fontWeight: 800, color: '#fff', marginBottom: 6 }}>Восстановление пароля</div>
                  <p style={{ fontSize: 13, color: '#a99fc7', lineHeight: 1.5, marginBottom: 14 }}>Укажите email — пришлём ссылку для сброса пароля.</p>
                  <div className="lp-field">
                    <label>Email</label>
                    <input className="lp-input" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
                  </div>
                  <button type="submit" className="lp-btn" disabled={loading}>{loading ? 'Отправка...' : 'Отправить ссылку'}</button>
                  <p className="lp-foot"><button type="button" onClick={() => setForgot(false)}>← Назад ко входу</button></p>
                </form>
              )
            ) : (
            <>
            <SegToggle
              className="dark full"
              value={mode}
              onChange={switchMode}
              options={[{ key: 'login', label: 'Вход' }, { key: 'register', label: 'Регистрация' }]}
            />

            {mode === 'login' ? (
              <form onSubmit={handleSubmit} noValidate>
                <div className="lp-field">
                  <label>Email</label>
                  <input className="lp-input" type="email" value={email}
                    onChange={e => setEmail(e.target.value)} placeholder="student@example.com" required />
                </div>

                <div className="lp-field">
                  <label>Пароль</label>
                  <div style={{ position: 'relative' }}>
                    <input className="lp-input" type={showPass ? 'text' : 'password'} value={password}
                      onChange={e => setPassword(e.target.value)} placeholder="••••••••" required
                      style={{ paddingRight: 40 }} />
                    <button type="button" className="lp-eye" onClick={() => setShowPass(!showPass)} aria-label="Показать пароль">
                      {showPass ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      )}
                    </button>
                  </div>
                  <div style={{ textAlign: 'right', marginTop: 7 }}>
                    <button type="button" onClick={() => { setForgot(true); setForgotSent(false); }} style={{ background: 'none', border: 'none', color: '#b794f6', fontWeight: 600, fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
                      Забыли пароль?
                    </button>
                  </div>
                </div>

                <button type="submit" className="lp-btn" disabled={loading}>
                  {loading ? 'Подождите...' : 'Войти'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <div className="lp-steps" aria-hidden="true">
                  <span className="lp-stepdot on" />
                  <span className={`lp-stepbar ${regStep >= 1 ? 'on' : ''}`} />
                  <span className={`lp-stepdot ${regStep >= 1 ? 'on' : ''}`} />
                </div>

                <div className="lp-stepwrap" key={regStep} data-dir={regDir}>
                  {regStep === 0 ? (
                    <>
                      <div className="lp-field">
                        <label>Имя</label>
                        <input className="lp-input" type="text" value={firstName}
                          onChange={e => setFirstName(e.target.value)} onKeyDown={onNameKey}
                          placeholder="Иван" />
                      </div>
                      <div className="lp-field">
                        <label>Фамилия</label>
                        <input className="lp-input" type="text" value={lastName}
                          onChange={e => setLastName(e.target.value)} onKeyDown={onNameKey}
                          placeholder="Иванов" />
                      </div>
                      <button type="button" className="lp-next" disabled={!nameValid} onClick={goNext}>
                        Далее
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" className="lp-back" onClick={goBack}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                        Назад
                      </button>
                      <div className="lp-greet">Приятно познакомиться, <b>{name.trim().split(/\s+/)[0]}</b>! Остался один шаг.</div>

                      <div className="lp-field">
                        <label>Email</label>
                        <input className="lp-input" type="email" value={email}
                          onChange={e => setEmail(e.target.value)} placeholder="student@example.com" required />
                      </div>

                      <div className="lp-field">
                        <label>Пароль</label>
                        <div style={{ position: 'relative' }}>
                          <input className="lp-input" type={showPass ? 'text' : 'password'} value={password}
                            onChange={e => setPassword(e.target.value)} placeholder="••••••••" required
                            style={{ paddingRight: 40 }} minLength={6} />
                          <button type="button" className="lp-eye" onClick={() => setShowPass(!showPass)} aria-label="Показать пароль">
                            {showPass ? (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                            ) : (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                            )}
                          </button>
                        </div>
                        <div className="lp-hint">Минимум 6 символов</div>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, margin: '4px 0 16px' }}>
                        <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 12.5, color: '#c9bff0', lineHeight: 1.45, cursor: 'pointer' }}>
                          <input type="checkbox" checked={agreePolicy} onChange={e => setAgreePolicy(e.target.checked)} style={{ marginTop: 2, accentColor: '#7c4dff', flexShrink: 0, width: 16, height: 16 }} />
                          <span>Ознакомлен(а) с{' '}
                            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#a78bfa', fontWeight: 600 }}>Политикой обработки персональных данных</a>
                          </span>
                        </label>
                        <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 12.5, color: '#c9bff0', lineHeight: 1.45, cursor: 'pointer' }}>
                          <input type="checkbox" checked={agreeConsent} onChange={e => setAgreeConsent(e.target.checked)} style={{ marginTop: 2, accentColor: '#7c4dff', flexShrink: 0, width: 16, height: 16 }} />
                          <span>Даю{' '}
                            <a href="/consent" target="_blank" rel="noopener noreferrer" style={{ color: '#a78bfa', fontWeight: 600 }}>Согласие на обработку персональных данных</a>
                          </span>
                        </label>
                      </div>

                      <button type="submit" className="lp-btn" disabled={loading || !agreePolicy || !agreeConsent}>
                        {loading ? 'Подождите...' : 'Зарегистрироваться'}
                      </button>
                    </>
                  )}
                </div>
              </form>
            )}

            <p className="lp-foot">
              {mode === 'login' ? (
                <>Нет аккаунта? <button type="button" onClick={() => switchMode('register')}>Зарегистрироваться</button></>
              ) : (
                <>Уже есть аккаунт? <button type="button" onClick={() => switchMode('login')}>Войти</button></>
              )}
            </p>

            {mode === 'login' && (
              <button type="button" className="lp-parent" onClick={gotoParent}>
                <IcParents />
                Я родитель — посмотреть успеваемость ученика
              </button>
            )}
            </>
            )}
          </div>
        </div>

        <button type="button" className="lp-scrollcue" onClick={gotoParent}>
          Для родителей
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        </button>
      </div>
      </div>{/* /.lp-top */}

      {/* ── РОДИТЕЛЬСКИЙ РАЗДЕЛ ── */}
      <div className="parent" ref={parentRef}>
        <div className="parent-inner">
          <div className="parent-head">
            <div className="parent-tag"><IcParents /> Для родителей</div>
            <h2 className="parent-title">Следите за успеваемостью <span>своего ребёнка</span></h2>
            <p className="parent-lead">
              Введите индивидуальный код ученика — и вы увидите его прогресс по курсу, просмотренные вебинары
              и статистику домашних заданий. Регистрация не требуется.
            </p>
          </div>

          {!child && (
            <div className="search-card" ref={searchRef}>
              <div className="sc-label">Код ученика</div>
              <div className="search-row">
                <div className="search-input-wrap">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input className="search-input" type="text" value={pQuery}
                    onChange={e => setPQuery((e.target.value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4))}
                    onKeyDown={e => { if (e.key === 'Enter') runSearch(); }}
                    placeholder="" maxLength={4}
                    style={{ textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700 }} />
                </div>
                <button className="search-btn" onClick={runSearch} disabled={ovLoading}>
                  {ovLoading ? 'Загружаем…' : 'Посмотреть успеваемость'}
                </button>
              </div>
              <div className="search-note">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                У каждого ученика есть свой код из 4 символов — он виден ученику в разделе «Профиль». Введите его, чтобы посмотреть успеваемость.
              </div>
            </div>
          )}

          {/* Результаты поиска */}
          {!child && results != null && (
            <div className="results">
              {results.length === 0 ? (
                <div className="res-empty">Введите код из 4 символов.<br />Его можно посмотреть у ученика в разделе «Профиль».</div>
              ) : (
                <>
                  <div className="results-label">Найдено: <b>{results.length}</b> — выберите профиль ребёнка</div>
                  {results.map(s => (
                    <button key={s.id} className="res-card" onClick={() => setChildId(s.id)}>
                      <div className="res-avatar" style={{ background: avaColor(s.id) }}>{initials(s.name)}</div>
                      <div className="res-info">
                        <div className="res-name">{s.name}</div>
                        <div className="res-meta">{s.course} · учится с {s.since}</div>
                      </div>
                      <span className="res-go"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></span>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}

          {/* Дашборд ребёнка */}
          {child && (
            <div className="child">
              <button className="child-back" onClick={backToSearch}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                К поиску
              </button>
              <div className="child-head">
                <div className="child-avatar" style={{ background: avaColor(child.id) }}>{initials(child.name)}</div>
                <div>
                  <div className="child-name">{child.name}</div>
                  <div className="child-email">{child.email}</div>
                  <span className="child-badge">{child.course}</span>
                </div>
              </div>

              <div className="stat-grid">
                <StatCard v={`${child.progress}%`} l="Прогресс курса" c="#b794f6" bg="rgba(124,77,255,.16)" />
                <StatCard v={`${child.lessonsDone}/${child.lessonsTotal}`} l="Уроков пройдено" c="#67e8f9" bg="rgba(34,184,207,.16)" />
                <StatCard v={child.hwAccepted} l="ДЗ принято" c="#6ee7b7" bg="rgba(34,197,94,.16)" />
                <StatCard v={child.avgGrade} l="Средний балл" c="#fcd34d" bg="rgba(245,158,11,.16)" />
              </div>

              <div className="child-bar-head"><span className="t">Прохождение курса</span><span className="v">{child.progress}%</span></div>
              <div className="child-bar"><i style={{ width: `${child.progress}%` }} /></div>

              <div className="child-cols">
                <div className="panel">
                  <div className="panel-title"><IcLayers /> Прогресс по разделам</div>
                  {child.modules.map((m, i) => (
                    <div className="mod-row" key={i}>
                      <div className="mod-row-head"><span className="mod-name">{m.name}</span><span className="mod-pct">{m.pct}%</span></div>
                      <div className="mini-bar"><i style={{ width: `${m.pct}%`, background: m.c }} /></div>
                    </div>
                  ))}
                </div>
                <div className="panel">
                  <div className="panel-title"><IcClock /> Последние домашние задания</div>
                  {child.hw.map((h, i) => {
                    const st = STATUS[h.s];
                    return (
                      <div className="hw-item" key={i}>
                        <span className="hw-dot" style={{ background: st.dot }} />
                        <div className="hw-info"><div className="hw-title">{h.t}</div><div className="hw-date">{h.d}</div></div>
                        <span className="hw-status" style={{ color: st.c, background: st.bg }}>{st.lbl}</span>
                      </div>
                    );
                  })}
                  <div className="hw-legend">
                    <span>● <b style={{ color: '#6ee7b7' }}>{child.hwAccepted}</b> принято</span>
                    <span>● <b style={{ color: '#fcd34d' }}>{child.hwPending}</b> на проверке</span>
                    <span>● <b style={{ color: '#f87171' }}>{child.hwRejected}</b> отклонено</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── ФУТЕР ── */}
      <footer className="lp-footer">
        <div className="footer-inner">
          <div>
            <div className="footer-logo" style={{ marginBottom: 16 }}>
              <img src={ASSET('logo-hq.png')} alt="ИНФА СОТКА" />
            </div>
            <div className="footer-legal">
              <b>ИП Пальчиков Артём Александрович</b><br />
              ИНН: 231131305845<br />
              ОГРНИП: 314231114800040
            </div>
          </div>
          <nav className="footer-docs">
            <div className="footer-docs-title">Документы</div>
            <a href="/privacy" target="_blank" rel="noopener noreferrer">Политика обработки персональных данных
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></a>
            <a href="/consent" target="_blank" rel="noopener noreferrer">Согласие на обработку персональных данных
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></a>
            <a href="/terms" target="_blank" rel="noopener noreferrer">Пользовательское соглашение
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></a>
          </nav>
        </div>
        <div className="footer-bottom">
          <span>© 2026 ИНФА СОТКА. Все права защищены.</span>
          <span>Подготовка к ОГЭ / ЕГЭ по информатике</span>
        </div>
      </footer>

      {/* Карточка ученика по коду — то же окно, что видит админ, но только чтение */}
      {overview?.student && (
        <StudentProfileModal
          student={overview.student}
          overview={overview}
          readOnly
          isOpen
          onClose={() => { setOverview(null); if (codeParam) navigate('/', { replace: true }); }}
        />
      )}
    </div>
  );
};

export default LandingPage;
